import { useCallback, useMemo } from 'react'

import usePricingExperimentsApi, { PricingExperiment } from '@/api/usePricingExperimentsApi'
import { resolveClient } from '@/lib/optimizely'

export interface UsePricingExperiments {
  pricingExperiments: PricingExperiment[]
  getCampaigns: () => Record<string, OptimizelyCampaign>
  getCampaign: (id: string) => OptimizelyCampaign | null
  getExperiments: () => Record<string, OptimizelyExperiment>
  getExperiment: (id: string) => OptimizelyExperiment | null
  getPricingExperiment: (id: string) => PricingExperiment | null
  getActiveExperiment: () => PricingExperiment | null
  getVariationId: () => string | undefined
  getVariationCampaignId: () => string | null
}

export const usePricingExperiments = (): UsePricingExperiments => {
  const { data: pricingExperiments = [] } = usePricingExperimentsApi()

  const getCampaigns = useCallback<() => Record<string, OptimizelyCampaign>>(
    () => resolveClient()?.get?.('state')?.getCampaignStates({ isActive: true }) ?? {},
    [],
  )

  const getExperiments = useCallback<() => Record<string, OptimizelyExperiment>>(
    () => resolveClient()?.get?.('state')?.getExperimentStates({ isActive: true }) ?? {},
    [],
  )

  const getCampaign = useCallback(
    (id: string): OptimizelyCampaign | null => getCampaigns()[id] ?? null,
    [getCampaigns],
  )

  const getExperiment = useCallback(
    (id: string): OptimizelyExperiment | null => getExperiments()[id] ?? null,
    [getExperiments],
  )

  const getPricingExperiment = useCallback(
    (id: string): PricingExperiment | null =>
      pricingExperiments.find((experiment) => experiment.campaignId === id) ?? null,
    [pricingExperiments],
  )

  const getActiveExperiment = useCallback(
    (): PricingExperiment | null =>
      Object.keys(getCampaigns()).map(getPricingExperiment).filter(Boolean)[0] ?? null,
    [getCampaigns, getPricingExperiment],
  )

  const getExperimentVariationId = useCallback(
    () => getExperiment(Object.keys(getExperiments())[0])?.variation?.id,
    [getExperiments, getExperiment],
  )

  const getVariationId = useCallback((): string | undefined => {
    const active = getActiveExperiment()

    const experimentVariationId = getExperimentVariationId()

    const variationId = getCampaign(active?.campaignId ?? '')?.experiment?.id

    const campaignVariationId =
      variationId && active?.pricingGroups.includes(variationId) ? variationId : undefined

    return campaignVariationId || experimentVariationId
  }, [getActiveExperiment, getCampaign, getExperimentVariationId])

  const getVariationCampaignId = useCallback(
    (): string | null => getActiveExperiment()?.campaignId ?? null,
    [getActiveExperiment],
  )

  return useMemo(
    () => ({
      pricingExperiments,
      getCampaigns,
      getCampaign,
      getPricingExperiment,
      getActiveExperiment,
      getVariationId,
      getVariationCampaignId,
      getExperiments,
      getExperiment,
    }),
    [
      pricingExperiments,
      getCampaigns,
      getCampaign,
      getPricingExperiment,
      getActiveExperiment,
      getVariationId,
      getVariationCampaignId,
      getExperiments,
      getExperiment,
    ],
  )
}
